import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Button } from "components/ui";
import styles from "./dialog.module.scss";
import Input, { CopyDisplayText } from "./Input";
import { FetchResult, useMutation } from "@apollo/client";
import { MUTATION_SSO_UPDATE } from "apps/company/api/graphQL/mutation";
import appInfo from "utility/app-info";
import { CommonModalProps } from "./common";
import { SSOItem } from "apps/company/pages/Integrations/dataTypes";
import { useAuth } from "providers/auth";
import { logError } from "utility/Function";
import { Trans } from "react-i18next";
import { useTranslation } from "lib/i18n/use-translation";

const OKTA_SETUP_GUIDE_URL =
  "https://www.notion.so/walkingonearth/Okta-Single-Sign-On-f677543f1625487ea41040d47971810c";

interface OktaDialogProps extends CommonModalProps {
  data: SSOItem;
  onAPIResponse: (response: FetchResult) => void;
}
const OktaDialog = ({
  data,
  onAPIResponse,
  onCloseAction,
  isOpen,
}: OktaDialogProps) => {
  const { t } = useTranslation(["admin"]);
  const [connectToOkta] = useMutation(MUTATION_SSO_UPDATE);
  const [clientId, setClientId] = useState<string>("");
  const [clientSecret, setClientSecret] = useState<string>("");
  const [redirectUrl, setRedirectUrl] = useState<string>("");
  const [errorText, setErrorText] = useState<string>("");
  const { companyAdmin } = useAuth();
  const handleAction = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (clientId && clientSecret && redirectUrl) {
      connectToOkta({
        variables: {
          appInfo,
          input: {
            authId: data.id,
            isSignInEnabled: !data.isEnabled,
            oktaSettings: {
              oktaClientId: clientId,
              oktaClientSecret: clientSecret,
              oktaClientApiUrl: redirectUrl,
            },
          },
        },
      })
        .then((result) => {
          toggle();
          onAPIResponse(result);
          reset();
        })
        .catch((error) => {
          setErrorText(error.message);
          logError("Error connecting to Okta", error);
        });
    } else {
      setErrorText(t("admin:integrations.okta.requiredFields"));
    }
  };
  const toggle = () => {
    reset();
    onCloseAction();
  };

  const reset = () => {
    setClientId("");
    setClientSecret("");
    setRedirectUrl("");
    setErrorText("");
  };

  return (
    <Modal
      isOpen={isOpen}
      style={{ width: "100%", maxWidth: "440px" }}
      className={styles["modal"]}
      contentClassName={styles["modal-content"]}
      centered
      data-testid="modal"
    >
      <form onSubmit={handleAction}>
        <ModalHeader className={styles["header"]} toggle={toggle}></ModalHeader>
        <ModalBody className={`${styles["body"]} ${styles.okta}`}>
          <p className={styles["title"]}>
            {t("admin:integrations.okta.title")}
          </p>
          <p className={`text-small ${styles["description"]}`}>
            <Trans
              i18nKey="admin:integrations.okta.description"
              components={[
                // Trans swaps these children for the catalog's link text; the
                // placeholder is what jsx-a11y/anchor-has-content reads
                // statically, and an expression keeps the i18n guard happy.
                <a href={OKTA_SETUP_GUIDE_URL} target="_blank" rel="noreferrer">
                  {"link"}
                </a>,
              ]}
            />
          </p>
          <div className={styles["inputs"]}>
            <CopyDisplayText
              title={t("admin:integrations.okta.redirectUriTitle")}
              text={`${process.env.REACT_APP_UPLOAD_URL}company/${companyAdmin.companyId}/complete/okta`}
            />
            <Input
              label={t("admin:integrations.okta.clientIdLabel")}
              value={clientId}
              type="text"
              placeholder={t("admin:integrations.okta.clientIdPlaceholder")}
              onChange={(e) => setClientId(e.target.value)}
              required
            />
            <Input
              value={clientSecret}
              label={t("admin:integrations.okta.clientSecretLabel")}
              placeholder={t("admin:integrations.okta.clientSecretPlaceholder")}
              type="text"
              onChange={(e) => setClientSecret(e.target.value)}
              required
            />
            <Input
              value={redirectUrl}
              label={t("admin:integrations.okta.domainLabel")}
              type="text"
              placeholder={t("admin:integrations.okta.domainPlaceholder")}
              onChange={(e) => setRedirectUrl(e.target.value)}
              required
            />
          </div>
          <p className={`sans ${styles["error"]}`}>{errorText}</p>
        </ModalBody>
        <ModalFooter className={styles["footer"]}>
          <Button variant="primary" className="sans" type="submit">
            {t("admin:integrations.okta.action")}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default OktaDialog;
